import * as React from 'react';
import { noop, GenericChartComponent, getMouseCanvas } from '@react-financial-charts/core';
import type { DrawingSharedState, HoverTextConfig, MoreProps } from '@/types/commontypes';
import { saveNodeType, isDefined } from 'react-financial-charts';
import { scaleLinear } from 'd3-scale';
import { max } from 'd3-array';
import { HoverTextNearMouse } from '@react-financial-charts/interactive/lib/components/index';
import { ClickableCircle } from '.';
import type { VolumeProfileRenderOptions, VPFRCacheKey } from '@/utils/volumeProfileRenderer';
import {
  VPFRCacheManager,
  getCandleHash,
  drawBackgroundBox,
  drawVolumeProfileBars,
  drawVolumeProfileLines,
  drawVolumeProfileValues,
} from '@/utils/volumeProfileRenderer';
import { volumeProfileDefaults } from '@/utils/defaultShapePropertyValues';
import { getNewXY } from '@/utils/utils';

interface EachAnchoredVolumeProfileProps {
  uuid: string;
  index?: number;
  anchorX: number;
  anchorY: number;
  appearance: Record<string, unknown>;
  selected?: boolean;
  locked?: boolean;
  hoverText?: Partial<HoverTextConfig>;
  onDrag?: (
    e: React.MouseEvent,
    uuid: string | undefined,
    newXYValue: { anchorX: number; lastCandleX?: number; firstCandleX?: number },
  ) => void;
  onDragComplete?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  onSelect?: (e: React.MouseEvent, uuid: string, moreProps: MoreProps) => void;
  onHover?: (uuid: string | null, moreProps: MoreProps) => void;
  onDoubleClick?: (e: React.MouseEvent) => void;
  edgeInteractiveCursor?: string;
  lineInteractiveCursor?: string;
  readonly sharedState: DrawingSharedState;
  readonly isAnyShapeEnabled?: boolean;
}

interface EachAnchoredVolumeProfileState {
  anchor?: string;
  hover?: boolean;
  dragging?: boolean;
  dragX?: number;
  dragY?: number;
}

export class EachAnchoredVolumeProfile extends React.Component<
  EachAnchoredVolumeProfileProps,
  EachAnchoredVolumeProfileState
> {
  public static defaultProps = {
    onDrag: noop,
    onSelect: noop,
    selected: false,
    edgeStroke: '#1e53e6',
    edgeFill: '#FFFFFF',
    edgeStrokeWidth: 2,
    r: 5,
    hoverText: {
      enable: false,
      bgWidth: 'auto' as string | number,
      bgHeight: 'auto' as string | number,
    },
    appearance: {
      ...volumeProfileDefaults,
    },
  };

  private dragStart!: { anchorX: number };
  private saveNodeType: <T>(type: string) => React.Ref<T>;
  private lineInteractive: string;
  private cacheManager = new VPFRCacheManager();

  public constructor(props: EachAnchoredVolumeProfileProps) {
    super(props);

    this.saveNodeType = saveNodeType.bind(this);
    this.lineInteractive = 'react-financial-charts-pointer-cursor';

    this.state = {
      hover: false,
      dragging: false,
    };
  }

  public componentWillUnmount() {
    window.removeEventListener('mouseup', this.resetDraggingOnMouseUp);
  }

  private isHover = (moreProps: MoreProps) => {
    const { appearance } = this.props;
    const {
      mouseXY,
      xScale,
      chartConfig: { yScale, height },
      fullData,
    } = moreProps;

    if (!mouseXY || !fullData || fullData.length === 0) return false;

    // 마우스가 차트 섹션 밖이면 호버 불가 (서브차트 간 이동 시 호버 고착 방지)
    if (mouseXY[1] < 0 || mouseXY[1] > height) return false;

    const [mouseX, mouseY] = mouseXY;

    // Get visible chart boundaries
    const visibleDomain = xScale.domain();
    const visibleLeftPx = xScale(visibleDomain[0]);
    const visibleRightPx = xScale(visibleDomain[1]);
    const visibleWidth = visibleRightPx - visibleLeftPx;

    // Calculate histogram area (visible chart area based)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const histogramWidth = (visibleWidth * ((appearance?.width as number | undefined) || 30)) / 100;
    let histogramStartX: number;
    let histogramEndX: number;

    if (appearance?.placement === 'right') {
      histogramStartX = visibleRightPx - histogramWidth;
      histogramEndX = visibleRightPx;
    } else {
      histogramStartX = visibleLeftPx;
      histogramEndX = visibleLeftPx + histogramWidth;
    }

    // Check if mouse is over histogram area (expanded hit area)
    const vpfrResult = this.cacheManager.getCachedOnly();
    if (vpfrResult) {
      const minYValue = vpfrResult.levels[0];
      const maxYValue = vpfrResult.levels[vpfrResult.levels.length - 1];
      const y1 = yScale(maxYValue);
      const y2 = yScale(minYValue);

      const histogramPadding = 5;
      if (
        mouseX >= histogramStartX - histogramPadding &&
        mouseX <= histogramEndX + histogramPadding &&
        mouseY >= Math.min(y1, y2) &&
        mouseY <= Math.max(y1, y2)
      ) {
        return true;
      }

      // Check POC/VAH/VAL lines (entire visible area)
      const lineHitDistance = 5;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      if ((appearance.poc as { show?: boolean } | undefined)?.show !== false) {
        const pocY = yScale(vpfrResult.poc);
        if (mouseX >= visibleLeftPx && mouseX <= visibleRightPx && Math.abs(mouseY - pocY) <= lineHitDistance) {
          return true;
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      if ((appearance.vah as { show?: boolean } | undefined)?.show !== false) {
        const vahY = yScale(vpfrResult.vah);
        if (mouseX >= visibleLeftPx && mouseX <= visibleRightPx && Math.abs(mouseY - vahY) <= lineHitDistance) {
          return true;
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      if ((appearance.val as { show?: boolean } | undefined)?.show !== false) {
        const valY = yScale(vpfrResult.val);
        if (mouseX >= visibleLeftPx && mouseX <= visibleRightPx && Math.abs(mouseY - valY) <= lineHitDistance) {
          return true;
        }
      }
    }

    return false;
  };

  public render() {
    const { anchorX, hoverText, selected, appearance, locked } = this.props;

    const {
      enable: hoverTextEnabled,
      selectedText: hoverTextSelectedStr,
      text: hoverTextUnselectedStr,
      ...restHoverTextProps
    } = { ...EachAnchoredVolumeProfile.defaultProps.hoverText, ...hoverText };

    const { hover, dragging } = this.state;

    if (dragging) {
      this.lineInteractive = 'react-financial-charts-grabbing-cursor';
    } else {
      this.lineInteractive = 'react-financial-charts-pointer-cursor';
    }

    /* eslint-disable @typescript-eslint/no-unsafe-type-assertion */
    const edgeStroke = (appearance?.edgeStroke as string | undefined) || '#1e53e6';
    const edgeFill = (appearance?.edgeFill as string | undefined) || '#FFFFFF';
    const edgeStrokeWidth = (appearance?.edgeStrokeWidth as number | undefined) || 2;
    const r = (appearance?.r as number | undefined) || 5;
    /* eslint-enable @typescript-eslint/no-unsafe-type-assertion */

    const actualR = locked ? r - 2 : r;
    const actualStroke = locked ? '#436fe9' : edgeStroke;

    return (
      <g>
        {/* Volume Profile Histogram - always rendered for consistent z-order */}
        <GenericChartComponent
          canvasDraw={this.drawAnchoredVolumeProfileCanvas}
          canvasToDraw={getMouseCanvas}
          drawOn={['mousemove', 'pan', 'drag']}
          onMouseDown={this.handleShapeMouseDown}
          isHover={this.isHover}
          onHover={this.handleHover}
          onUnHover={this.handleUnHover}
          interactiveCursorClass={this.lineInteractive}
          selected={selected || hover}
        />

        {/* Control Point - rendered last so it appears on top */}
        <ClickableCircle
          ref={this.saveNodeType('anchor')}
          show={selected || hover || locked}
          cx={dragging && this.state.dragX !== undefined ? this.state.dragX : anchorX}
          cy={dragging && this.state.dragY !== undefined ? this.state.dragY : this.getControlPointY()}
          r={actualR}
          fillStyle={edgeFill}
          strokeStyle={actualStroke}
          strokeWidth={edgeStrokeWidth}
          interactiveCursorClass="react-financial-charts-move-cursor"
          onDragStart={!locked ? this.handleDragStart : noop}
          onDrag={!locked ? this.handleDrag : noop}
          onDragComplete={!locked ? this.handleDragComplete : noop}
        />

        {/* Hover Text */}
        <HoverTextNearMouse
          show={!!hoverTextEnabled && (hover ?? false)}
          {...restHoverTextProps}
          text={selected ? hoverTextSelectedStr : hoverTextUnselectedStr}
        />
      </g>
    );
  }

  private getControlPointY(): number {
    // Return POC Y position (cached value or anchorY)
    const vpfrResult = this.cacheManager.getCachedOnly();
    return vpfrResult?.poc || this.props.anchorY;
  }

  /**
   * Canvas draw wrapper - draws volume profile OR dragging indicator based on state
   * This ensures consistent render order (control point always on top)
   */
  private readonly drawAnchoredVolumeProfileCanvas = (ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
    if (this.state.dragging) {
      this.drawDraggingIndicator(ctx, moreProps);
    } else {
      this.drawAnchoredVolumeProfile(ctx, moreProps);
    }
  };

  private readonly drawDraggingIndicator = (ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
    const { dragX } = this.state;
    if (dragX === undefined) return;

    const {
      xScale,
      chartConfig: { yScale },
      fullData,
      xAccessor,
    } = moreProps;

    // Clamp dragX to lastCandleX (can't drag beyond current candle)
    const lastCandleX = fullData && fullData.length > 0 ? xAccessor(fullData[fullData.length - 1]) : dragX;
    const clampedDragX = Math.min(dragX, lastCandleX);

    const screenX = xScale(clampedDragX);
    const chartHeight = yScale.range();
    const chartTop = Math.min(...chartHeight);
    const chartBottom = Math.max(...chartHeight);

    // Draw vertical line at drag position
    ctx.save();
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 1;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(screenX, chartTop);
    ctx.lineTo(screenX, chartBottom);
    ctx.stroke();
    ctx.restore();
  };

  private readonly drawAnchoredVolumeProfile = (ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
    const { anchorX, appearance } = this.props;

    if (!appearance?.volumeProfile) return;

    const {
      xScale,
      chartConfig: { yScale },
      fullData,
      xAccessor,
    } = moreProps;

    if (!fullData || fullData.length === 0) return;

    // Get last candle X (dynamic end - always latest candle)
    const lastCandleX = xAccessor(fullData[fullData.length - 1]);

    // Calculate indices from x values
    const minX = Math.min(anchorX, lastCandleX);
    const maxX = Math.max(anchorX, lastCandleX);
    const startIndex = fullData?.findIndex(d => xAccessor(d) >= minX) ?? -1;
    const endIndex = fullData?.findIndex(d => xAccessor(d) >= maxX) ?? -1;

    if (startIndex === -1 || endIndex === -1 || startIndex === endIndex) return;

    // Calculate VPFR with caching
    // Cache key includes last candle hash for real-time updates
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const rows = (appearance.rowSize as number | undefined) || 7;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const valueAreaPercent = (appearance.valueAreaVolume as number | undefined) || 70;
    const lastCandle = fullData[fullData.length - 1];
    const cacheKey: VPFRCacheKey = {
      anchorX: minX,
      lastCandleX: maxX,
      rowSize: rows,
      valueAreaVolume: valueAreaPercent,
      dataLength: fullData?.length || 0,
      lastCandleHash: getCandleHash(lastCandle),
    };
    const vpfrResult = this.cacheManager.getOrCalculate(
      fullData,
      startIndex,
      endIndex,
      rows,
      valueAreaPercent,
      cacheKey,
    );
    if (!vpfrResult || vpfrResult.totalVolumes.length === 0) return;

    // Get visible chart boundaries
    const visibleDomain = xScale.domain();
    const visibleLeftPx = xScale(visibleDomain[0]);
    const visibleRightPx = xScale(visibleDomain[1]);
    const visibleWidth = visibleRightPx - visibleLeftPx;

    // Histogram placement: based on VISIBLE CHART AREA (not selection box)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const histogramWidth = (visibleWidth * ((appearance.width as number | undefined) || 30)) / 100;

    let histogramStartX: number;
    let histogramEndX: number;
    if (appearance.placement === 'right') {
      histogramStartX = visibleRightPx - histogramWidth;
      histogramEndX = visibleRightPx;
    } else {
      histogramStartX = visibleLeftPx;
      histogramEndX = visibleLeftPx + histogramWidth;
    }

    // Box coordinates (for background)
    const boxLeftX = xScale(minX);
    const boxRightX = xScale(maxX);

    // Create volume scale
    const maxVolumeValue = max(vpfrResult.totalVolumes) || 1;
    const isRight = appearance.placement === 'right';

    const volumeScale = scaleLinear()
      .domain([0, maxVolumeValue])
      .range(isRight ? [histogramEndX, histogramStartX] : [histogramStartX, histogramEndX]);

    // Render options
    const renderOptions: VolumeProfileRenderOptions = {
      appearance,
      vpfrResult,
      yScale,
      rows,
      histogramStartX,
      histogramEndX,
      isRightPlacement: isRight,
      // Lines span ENTIRE VISIBLE AREA
      lineLeftX: visibleLeftPx,
      lineRightX: visibleRightPx,
      // Background box: selection range only
      boxLeftX,
      boxRightX,
    };

    ctx.save();

    // 1. Draw background box (selection range)
    drawBackgroundBox(ctx, renderOptions);

    // 2. Draw volume bars
    drawVolumeProfileBars(ctx, renderOptions, volumeScale);

    // 3. Draw POC/VAH/VAL lines (entire visible area)
    drawVolumeProfileLines(ctx, renderOptions);

    // 4. Draw values labels
    drawVolumeProfileValues(ctx, renderOptions);

    ctx.restore();
  };

  // Safety: ensure dragging is always reset on mouseup
  private readonly resetDraggingOnMouseUp = () => {
    if (this.state.dragging) this.setState({ dragging: false, dragX: undefined, dragY: undefined });
  };

  private readonly handleDragStart = () => {
    this.dragStart = {
      anchorX: this.props.anchorX,
    };
    const initialY = this.getControlPointY();
    if (!this.state.dragging) {
      this.setState({ dragging: true, hover: true, dragX: this.props.anchorX, dragY: initialY });
    }
    window.addEventListener('mouseup', this.resetDraggingOnMouseUp, { once: true });
  };

  private readonly handleDrag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { locked, onDrag, uuid } = this.props;
    if (locked) return;

    const { fullData, xAccessor } = moreProps;
    if (!fullData || fullData.length === 0) return;

    // Use getNewXY to snap to candle center (like FRVP)
    const [snappedX, snappedY] = getNewXY(moreProps);

    // Get data bounds
    const lastCandleX = xAccessor(fullData[fullData.length - 1]);
    const firstCandleX = xAccessor(fullData[0]);

    // Clamp to data range (can't drag beyond current candle)
    const clampedX = Math.max(firstCandleX, Math.min(snappedX, lastCandleX));

    // Update drag position for visual feedback
    this.setState({ dragX: clampedX, dragY: snappedY });

    onDrag?.(e, uuid, {
      anchorX: clampedX,
      lastCandleX,
      firstCandleX,
    });
  };

  private readonly handleDragComplete = (e: React.MouseEvent, moreProps: MoreProps) => {
    window.removeEventListener('mouseup', this.resetDraggingOnMouseUp);
    if (this.state.dragging) this.setState({ dragging: false, dragX: undefined, dragY: undefined });
    const { onDragComplete } = this.props;
    if (onDragComplete) {
      onDragComplete(e, moreProps);
    }
  };

  private readonly handleHover = (_: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onHover, sharedState, isAnyShapeEnabled } = this.props;
    if (sharedState?.status === 'started' || isAnyShapeEnabled) return;
    const hovering = moreProps.hovering;

    if (this.state.hover !== hovering) {
      this.setState({ hover: hovering });

      if (onHover) {
        onHover(hovering ? (uuid ?? null) : null, moreProps);
      }
    }
  };

  private readonly handleShapeMouseDown = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { onSelect, uuid, sharedState } = this.props;
    if (sharedState?.status === 'started') return;
    if (onSelect && moreProps?.hovering && isDefined(uuid)) {
      onSelect(e, uuid, moreProps);
    }
  };

  private readonly handleUnHover = (_: React.MouseEvent, moreProps: MoreProps) => {
    const { onHover } = this.props;

    if (this.state.hover !== false) {
      this.setState({ hover: false });
    }

    if (onHover) {
      onHover(null, moreProps);
    }
  };
}
